import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

function AddressChange({ onClose, userId }) {
    const [formData, setFormData] = useState({
        name: '',
        mobile: '',
        pincode: '',
        locality: '',
        // 🔹 Separate address components
        houseNo: '',
        buildingName: '',
        street: '',
        landmark: '',
        area: '',
        // Existing fields
        city: '',
        district: '',
        state: '',
        addressType: 'Home',
        ledcode: userId
    });

    const STATE_MAP = {
        AN: "Andaman and Nicobar Islands",
        AP: "Andhra Pradesh",
        AR: "Arunachal Pradesh",
        AS: "Assam",
        BR: "Bihar",
        CG: "Chandigarh",
        CH: "Chhattisgarh",
        DN: "Dadra and Nagar Haveli",
        DD: "Daman and Diu",
        DL: "Delhi",
        GA: "Goa",
        GJ: "Gujarat",
        HR: "Haryana",
        HP: "Himachal Pradesh",
        JK: "Jammu and Kashmir",
        JH: "Jharkhand",
        KA: "Karnataka",
        KL: "Kerala",
        LA: "Ladakh",
        LD: "Lakshadweep",
        MP: "Madhya Pradesh",
        MH: "Maharashtra",
        MN: "Manipur",
        ML: "Meghalaya",
        MZ: "Mizoram",
        NL: "Nagaland",
        OR: "Odisha",
        PY: "Puducherry",
        PB: "Punjab",
        RJ: "Rajasthan",
        SK: "Sikkim",
        TN: "Tamil Nadu",
        TS: "Telangana",
        TR: "Tripura",
        UP: "Uttar Pradesh",
        UK: "Uttarakhand",
        WB: "West Bengal"
    };


    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 🔹 Fetch City & District from PIN
    const fetchPinDetails = async (pin) => {
        if (pin.length !== 6) return;

        try {
            const res = await axios.get(
                `${apiBaseUrl}/delhivery-pincode/${pin}`
            );
            const pinInfo = res.data?.data?.delivery_codes?.[0]?.postal_code;

            // console.log(pinInfo, 'pinInfo');


            if (pinInfo) {
                const stateName = STATE_MAP[pinInfo.state_code]

                setFormData(prev => ({
                    ...prev,
                    city: pinInfo.city || prev.city,
                    district: pinInfo.district || prev.district,
                    area: pinInfo.area || prev.area,
                    locality: pinInfo.locality || prev.locality,
                    state: stateName || prev.state
                }));
            }
        } catch (err) {
            console.error("PIN fetch failed:", err);
        }
    };

    // 🔹 Enhanced validation with separate fields
    const validateAddress = () => {
        if (!formData.name.trim()) {
            toast.error('Name is required');
            return false;
        }

        if (!/^\d{10}$/.test(formData.mobile)) {
            toast.error('Enter valid 10-digit mobile number');
            return false;
        }

        if (!/^\d{6}$/.test(formData.pincode)) {
            toast.error('Enter valid 6-digit pincode');
            return false;
        }

        if (!formData.locality.trim()) {
            toast.error('Locality / Town is required');
            return false;
        }

        // Validate separate address components
        if (!formData.houseNo.trim()) {
            toast.error('House/Flat number is required');
            return false;
        }

        if (!formData.area.trim()) {
            toast.error('Area/Locality is required');
            return false;
        }

        if (!formData.city.trim()) {
            toast.error('City is required');
            return false;
        }

        if (!formData.district.trim()) {
            toast.error('District is required');
            return false;
        }

        if (!formData.state.trim()) {
            toast.error('State is required');
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateAddress()) return;

        // Prepare data for backend (sending all separate components)
        const addressData = {
            name: formData.name,
            mobile: formData.mobile,
            pincode: formData.pincode,
            locality: formData.locality || null,
            houseNo: formData.houseNo,
            buildingName: formData.buildingName || null,
            street: formData.street || null,
            landmark: formData.landmark || null,
            area: formData.area,
            city: formData.city,
            district: formData.district,
            state: formData.state,
            addressType: formData.addressType,
            ledcode: formData.ledcode
        };

        try {
            const response = await axios.post(
                `${apiBaseUrl}/user/addAddress`,
                addressData
            );
            toast.success(response.data.message || "Address added successfully");
            onClose();
        } catch (error) {
            console.error('Error saving address:', error);
            const errorMsg = error.response?.data?.error || 'Failed to save address';
            toast.error(errorMsg);
        }
    };

    // 🔹 Build address preview for user to see
    const buildAddressPreview = () => {
        const parts = [];
        if (formData.houseNo) parts.push(formData.houseNo);
        if (formData.buildingName) parts.push(formData.buildingName);
        if (formData.street) parts.push(formData.street);
        if (formData.landmark) parts.push(formData.landmark);
        if (formData.area) parts.push(formData.area);

        return parts.join(', ');
    };

    const addressPreview = buildAddressPreview();

    return (
        <div className='fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50'>
            <div className='lg:w-1/2 w-full bg-white rounded-lg p-6 relative max-h-[90vh] overflow-y-auto'>
                <button
                    onClick={onClose}
                    className='absolute top-2 right-2 bg-gray-200 hover:bg-gray-300 rounded-full w-8 h-8 flex items-center justify-center'
                >
                    &times;
                </button>

                <div className='flex flex-col gap-5'>
                    <h2 className='text-xl font-bold text-gray-800'>ADD NEW ADDRESS</h2>
                    <hr className='border-gray-300' />

                    <div>
                        <h3 className='font-semibold text-gray-700 mb-4'>CONTACT DETAILS</h3>

                        <div className='space-y-4'>
                            <div>
                                <label className='block text-sm font-medium text-gray-600 mb-1'>
                                    Full Name *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    // placeholder="Enter your full name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className='w-full h-[42px] border border-gray-300 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                    required
                                />
                            </div>

                            <div>
                                <label className='block text-sm font-medium text-gray-600 mb-1'>
                                    Mobile Number *
                                </label>
                                <input
                                    type="text"
                                    name="mobile"
                                    // placeholder="10-digit mobile number"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    className='w-full h-[42px] border border-gray-300 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                    maxLength="10"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className='font-semibold text-gray-700 mb-4'>ADDRESS DETAILS</h3>

                        <div className='space-y-4'>
                            <div className='grid grid-cols-2 gap-4'>
                                <div>
                                    <label className='block text-sm font-medium text-gray-600 mb-1'>
                                        Pincode *
                                    </label>
                                    <input
                                        type="text"
                                        name="pincode"
                                        // placeholder="6-digit pincode"
                                        value={formData.pincode}
                                        onChange={(e) => {
                                            handleChange(e);
                                            fetchPinDetails(e.target.value);
                                        }}
                                        className='w-full h-[42px] border border-gray-300 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                        maxLength="6"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className='block text-sm font-medium text-gray-600 mb-1'>
                                        Locality / Town *
                                    </label>
                                    <input
                                        type="text"
                                        name="locality"
                                        // placeholder="Enter locality/town"
                                        value={formData.locality}
                                        onChange={handleChange}
                                        className='w-full h-[42px] border border-gray-300 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                    />
                                </div>
                            </div>

                            {/* 🔹 Separate Address Components */}
                            <div className='space-y-4'>
                                <div className='grid grid-cols-2 gap-4'>
                                    <div>
                                        <label className='block text-sm font-medium text-gray-600 mb-1'>
                                            House/Flat No. *
                                        </label>
                                        <input
                                            type="text"
                                            name="houseNo"
                                            // placeholder="e.g., 123, Flat 4A"
                                            value={formData.houseNo}
                                            onChange={handleChange}
                                            className='w-full h-[42px] border border-gray-300 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className='block text-sm font-medium text-gray-600 mb-1'>
                                            Building/Apartment Name
                                        </label>
                                        <input
                                            type="text"
                                            name="buildingName"
                                            // placeholder="e.g., Sunshine Apartments"
                                            value={formData.buildingName}
                                            onChange={handleChange}
                                            className='w-full h-[42px] border border-gray-300 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                        />
                                    </div>
                                </div>



                                <div className='grid grid-cols-2 gap-4'>
                                    <div>
                                        <label className='block text-sm font-medium text-gray-600 mb-1'>
                                            Street/Road Name
                                        </label>
                                        <input
                                            type="text"
                                            name="street"
                                            // placeholder="e.g., MG Road, Park Street"
                                            value={formData.street}
                                            onChange={handleChange}
                                            className='w-full h-[42px] border border-gray-300 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                        />
                                    </div>
                                    <div>
                                        <label className='block text-sm font-medium text-gray-600 mb-1'>
                                            Landmark (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            name="landmark"
                                            // placeholder="e.g., Near City Mall"
                                            value={formData.landmark}
                                            onChange={handleChange}
                                            className='w-full h-[42px] border border-gray-300 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                        />
                                    </div>
                                </div>

                                <div className='grid grid-cols-2 gap-4'>
                                    <div>
                                        <label className='block text-sm font-medium text-gray-600 mb-1'>
                                            Area/Locality *
                                        </label>
                                        <input
                                            type="text"
                                            name="area"
                                            // placeholder="e.g., Koramangala, Bandra West"
                                            value={formData.area}
                                            onChange={handleChange}
                                            className='w-full h-[42px] border border-gray-300 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                            required
                                        />
                                    </div>
                                   <div>
                                    <label className='block text-sm font-medium text-gray-600 mb-1'>
                                        City *
                                    </label>
                                    <input
                                        type="text"
                                        name="city"
                                        // placeholder="e.g., Mumbai, Bangalore"
                                        value={formData.city}
                                        onChange={handleChange}
                                        className='w-full h-[42px] border border-gray-300 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                        required
                                    />
                                </div>
                                </div>
                            </div>

                            <div className='grid grid-cols-2 gap-4'>
                                <div>
                                    <label className='block text-sm font-medium text-gray-600 mb-1'>
                                        District *
                                    </label>
                                    <input
                                        type="text"
                                        name="district"
                                        // placeholder="e.g., Malappuram"
                                        value={formData.district}
                                        onChange={handleChange}
                                        className='w-full h-[42px] border border-gray-300 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                        required
                                    />
                                </div>
                                <div>
                                    <label className='block text-sm font-medium text-gray-600 mb-1'>
                                        State *
                                    </label>
                                    <input
                                        type="text"
                                        name="state"
                                        // placeholder="e.g., Kerala"
                                        value={formData.state}
                                        onChange={handleChange}
                                        className='w-full h-[42px] border border-gray-300 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 🔹 Address Preview */}
                    {/* <div className='mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200'>
            <h4 className='font-semibold text-gray-700 mb-3'>Address Preview</h4>
            <div className='p-4 bg-white rounded border border-gray-300'>
              {addressPreview ? (
                <>
                  <p className='text-gray-800 font-medium mb-1'>{addressPreview}</p>
                  {formData.city && (
                    <p className='text-gray-600 text-sm'>
                      {formData.city}
                      {formData.district && `, ${formData.district}`}
                      {formData.pincode && ` - ${formData.pincode}`}
                    </p>
                  )}
                </>
              ) : (
                <p className='text-gray-400 italic'>Complete the address details to see preview</p>
              )}
            </div>
            <div className='text-xs text-gray-500 mt-3 flex items-center gap-2'>
              <span className='inline-block w-3 h-3 bg-green-500 rounded-full'></span>
              Each component will be stored separately in the database
            </div>
          </div> */}

                    <div>
                        <h4 className='font-semibold text-gray-700 mb-3'>ADDRESS TYPE</h4>
                        <div className='flex gap-4'>
                            <label className='flex items-center gap-3 cursor-pointer p-4 rounded-lg border border-gray-300 hover:bg-gray-50 flex-1'>
                                <input
                                    type="radio"
                                    name="addressType"
                                    value="Home"
                                    checked={formData.addressType === 'Home'}
                                    onChange={handleChange}
                                    className='w-5 h-5 text-blue-600'
                                />
                                <div>
                                    <span className='font-medium text-gray-700'>Home</span>
                                    <p className='text-xs text-gray-500 mt-1'>For residential deliveries</p>
                                </div>
                            </label>
                            <label className='flex items-center gap-3 cursor-pointer p-4 rounded-lg border border-gray-300 hover:bg-gray-50 flex-1'>
                                <input
                                    type="radio"
                                    name="addressType"
                                    value="Office"
                                    checked={formData.addressType === 'Office'}
                                    onChange={handleChange}
                                    className='w-5 h-5 text-blue-600'
                                />
                                <div>
                                    <span className='font-medium text-gray-700'>Office</span>
                                    <p className='text-xs text-gray-500 mt-1'>For workplace deliveries</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className='flex gap-4 mt-6'>
                        <button
                            onClick={handleSubmit}
                            className='flex-1 h-[50px] bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            Save Address
                        </button>
                        <button
                            onClick={onClose}
                            className='flex-1 h-[50px] border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors'
                        >
                            Cancel
                        </button>
                    </div>

                    <div className='text-xs text-gray-500 mt-2'>
                        <p className='flex items-center gap-2'>
                            <span className='text-red-500'>*</span> Required fields must be filled for successful delivery
                        </p>
                        {/* <p className='mt-1 text-blue-600'>
              <i>Note: Address components are stored separately for better delivery accuracy</i>
            </p> */}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddressChange;
